import { useTranslation } from '@/i18n/client';
import {
  ADDRESS_CATEGORY_VALUES,
  ADDRESS_EVENT_NAMES,
  addressBusinessLogger,
} from '@/utils/logger/instance/addressBusinessLogger';
import { DialogContent, DialogTitle, TextField } from '@mui/material';
import to from 'await-to-js';
import cn from 'clsx';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';

import { useStores } from '@/app/csr-src/models';
import UKDialog from '@/components/UKDialog';
import UKLoadingIcon, { CIRCLE_COLOR_MAP } from '@/components/UKLoadingIcon';
import UKToast from '@/components/UKToast/index';
import UKUpload from '@/components/UKUpload';
import IMAGEAPI_TYPE from '@/constants/imageApiType';

import AddressAdd from '../addressAdd';
import AddressSelect from '../addressSelect';
import CommonButton from '../commonButton';
import CommonSelect from '../commonSelect';
import DateSelect from '../dateSelect';
import LocationModal from '../locationModal';
import RuleWrap from '../ruleWrap';
import styles from './index.module.scss';

import {
  fetchAddressList,
  fetchApplyReasonList,
  fetchPostHouseList,
  fetchRecordApplyInfo,
  recordApplyCreate,
} from '@/apis/afterSales/afterSales.api';
import {
  AddressInfo,
  AddressVo,
  ApplyReasonResVo,
  IRecordApplyInfoReq,
  PostHouseListResVo,
  RecordApplyInfoResVo,
  ReturnCustomerInfoVo,
} from '@/apis/afterSales/afterSales.interface';

// 图标
import Expand from '@/assets/icons/chevron-down.svg';
import LocationIco from '@/assets/icons/LocationIco.svg';
import UKPageNothing, { DefaultImageType } from '@/components/UKPageNothing';

/* eslint-disable @typescript-eslint/no-explicit-any */
interface refundModalProps {
  orderId: string;
  isOpen: boolean;
  closeHandler: () => void;
  handleConfirmClick: (val: any) => void;
  postCode: string;
  skuIds?: string;
}

const ReturnRequestModal: React.FC<refundModalProps> = observer(
  ({ orderId, isOpen, closeHandler, handleConfirmClick, postCode, skuIds }) => {
    const { t } = useTranslation();
    const { AfterSalesStore } = useStores();

    // apply info
    const [applyInfo, setApplyInfo] = useState<RecordApplyInfoResVo | null>(null);

    // Pick-up address
    const [addressList, setAddressList] = useState<AddressVo[] | []>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false); // 提交接口loading

    // reason
    const [reasonList, setReasonList] = useState<ApplyReasonResVo[] | []>([]);

    const [locationList, setLocationList] = useState<PostHouseListResVo[] | []>([]);

    const [curReason, setCurReason] = useState('');
    // 默认选中Drop-off
    const [curMethod, setCurMethod] = useState('');
    const [curLocation, setCurLocation] = useState('');
    const [curLocationInfo, setCurLocationInfo] = useState<PostHouseListResVo | null>(null);
    const [curAddress, setCurAddress] = useState('');
    const [defaultAddress, setDefaultAddress] = useState<ReturnCustomerInfoVo | null>(null);
    const [isDefault, setIsDefault] = useState(true);
    const curPickupTime = useRef(null);
    const [isLocationDefault, setIsLocationDefault] = useState(true);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);

    const [inputValue, setInputValue] = useState('');
    const maxCharacters = 500; // 设置最大字符数

    const [imageList, setImageList] = useState<string[] | undefined>();
    const [videoList, setVideoList] = useState<string[] | null>();

    // 查询地址列表
    const loadAddressList = async () => {
      const [err, addressListData] = await to(fetchAddressList());

      if (err) {
        // setError("Failed to fetch address list");
        setAddressList([]);
      } else {
        setAddressList(addressListData as AddressVo[]);
        // setError(null);
      }
      if (!addressListData?.length) {
        // 地址列表查询异常上报
        addressBusinessLogger({
          eventName: ADDRESS_EVENT_NAMES.ADDRESS_LIST_QUERY_ERROR,
          category: {
            module: ADDRESS_CATEGORY_VALUES.MODULE.AFTER_SALES,
            action: ADDRESS_CATEGORY_VALUES.ACTION.REQUEST,
            result: ADDRESS_CATEGORY_VALUES.RESULT.ERROR,
          },
        });
      }
    };

    // 查询支持的售后原因列表
    const loadReasonList = async () => {
      const [err, reasonListData] = await to(
        fetchApplyReasonList({
          reasonApplyType: 1,
        }),
      );

      if (err) {
        // setError("Failed to fetch address list");
        setReasonList([]);
      } else {
        const formatReasonListData = reasonListData?.map((item) => {
          return { ...item, reasonId: String(item.reasonId) };
        });
        setReasonList(formatReasonListData as ApplyReasonResVo[]);
        // setError(null);
      }
    };

    // 获取驿站信息列表
    const loadPostHouseList = async (addressInfo: AddressInfo) => {
      const [err, postHouseListData] = await to(fetchPostHouseList(getParamsMap(addressInfo)));

      if (err) {
        // setError("Failed to fetch address list");
        setLocationList([]);
      } else {
        setLocationList(postHouseListData as PostHouseListResVo[]);
        postHouseListData?.[0]?.postHouseId && setCurLocation(postHouseListData?.[0]?.postHouseId);
        postHouseListData?.[0] && setCurLocationInfo(postHouseListData?.[0]);
        // setError(null);
      }
    };

    // 查询进入售后单申请详情页，订单以及退款相关信息
    const loadApplyInfo = async () => {
      const requestData: IRecordApplyInfoReq = {
        orderId: String(orderId),
        skuInfoList: AfterSalesStore?.selectedProductList,
      };
      const [err, applyInfoData] = await to(fetchRecordApplyInfo(requestData));

      if (err) {
        setError('Failed to fetch order details');
      } else {
        setApplyInfo(applyInfoData);
        // Pick-up Address 默认选中订单的收货地址
        if (applyInfoData?.returnCustomerInfo?.addressInfo?.addressId) {
          setCurAddress(String(applyInfoData?.returnCustomerInfo?.addressInfo?.addressId));
        }
        setDefaultAddress(applyInfoData?.returnCustomerInfo);
        if (applyInfoData?.returnModelList?.length) {
          setCurMethod(String(applyInfoData?.returnModelList[0]?.type));
          // setCurMethod("70");
        }
        loadPostHouseList(applyInfoData?.returnCustomerInfo?.addressInfo);
        setError(null);
      }
      setLoading(false);
    };

    useEffect(() => {
      loadApplyInfo();
      loadAddressList();
      loadReasonList();
    }, []);

    const getParamsMap = (addressInfo: AddressInfo | undefined) => {
      return {
        postCode: addressInfo?.postCode || '',
        firstAddressId: addressInfo?.provinceId,
        firstAddressName: addressInfo?.provinceName,
        secondAddressId: addressInfo?.cityId,
        secondAddressName: addressInfo?.cityName,
        thirdAddressId: addressInfo?.countyId,
        fourthAddressId: addressInfo?.townId,
        addressDetail: addressInfo?.addressDetail,
        houseNumber: addressInfo?.houseNumber,
        orderId,
      };
    };

    const selectChangeHandler = (val: string) => {
      setCurReason(val);
    };

    const locationChangeHandler = (val: any) => {
      setIsLocationDefault(false);
      setShowLocationModal(false);
      setCurLocation(val.postHouseId);
      setCurLocationInfo(val);
    };

    const addressChangeHandler = (val: string) => {
      curPickupTime.current = null;
      setIsDefault(false);
      setCurAddress(val);
    };

    const timeChangeHandler = (val: any) => {
      curPickupTime.current = val;
    };

    // 保存地址
    const saveCloseHandler = () => {
      loadAddressList();
      setShowAddModal(false);
    };

    // 输入框输入
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value.length <= maxCharacters) {
        // 限制输入字符数
        setInputValue(value);
      }
    };

    // 图片上传
    const onUploadMedia = (mediaList: Record<string, string[]> | null) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (mediaList?.length) {
        setImageList(mediaList as any);
      }
      // setCurrentSkuMedia({ ...(currentSkuMedia || {}), [skuId]: mediaList } as any);
    };

    // const isBtnEnable = () => {
    //   let enable = false;
    //   if (curReason && curMethod && inputValue?.length >= 40) {
    //     if ((curMethod === "70" && curLocation) || (curMethod === "4" && curAddress)) {
    //       enable = true;
    //     }
    //   }
    //   return enable;
    // };

    // 当前选中的地址信息
    const selectedAddressInfo = addressList.find((ele) => String(ele.addressId) === curAddress);

    // 创建售后服务单
    const createRecord = async () => {
      // 增加校验
      if (!curReason) {
        UKToast.show({
          icon: 'warning',
          content: t('order.orderDetail.returnRequestModal.reasonValidator'),
        });
        return;
      }
      // // 描述必须大于40个字符
      // if (!(inputValue?.length >= 40)) {
      //   UKToast.show({
      //     icon: "warning",
      //     content: "Please provide a detailed reason of at least 40 characters",
      //   });
      //   return;
      // }
      if (!curMethod) {
        UKToast.show({
          icon: 'warning',
          content: t('order.orderDetail.returnRequestModal.methodValidator'),
        });
        return;
      }
      // 暂时注释前端校验
      // if (curMethod === "70" && !curLocation) {
      //   UKToast.show({
      //     icon: "warning",
      //     content: "Please select a Drop-off Location",
      //   });
      //   return;
      // }
      if (curMethod === '4' && !curAddress) {
        UKToast.show({
          icon: 'warning',
          content: t('order.orderDetail.returnRequestModal.addressValidator'),
        });
        return;
      }
      if (curMethod === '4' && !curPickupTime.current) {
        UKToast.show({
          icon: 'warning',
          content: t('order.orderDetail.returnRequestModal.pickUpTimeSelect'),
        });
        return;
      }
      setSubmitLoading(true);
      const customerExpect = applyInfo?.skuList?.[0]?.serviceTypeList?.[0]?.serviceType;
      const wareList =
        AfterSalesStore?.selectedProductList?.map(
          ({
            skuId,
            skuUUid,
            skuName,
            productType,
            applyCount,
            venderId,
          }: {
            skuId: string;
            skuUUid: string;
            skuName: string;
            productType: number;
            applyCount: number;
            venderId: string;
          }) => ({
            skuId,
            skuUUid,
            skuName,
            productType,
            applyCount,
            venderId,
          }),
        ) || [];
      const refundType = applyInfo?.refundTypeList?.[0]?.type;

      const { contactName, contactTel, contactTelCountry, addressInfo } =
        applyInfo?.returnCustomerInfo || {};
      let pickupParams = {};
      if (curMethod === '70') {
        // 选择某个驿站
        if (curLocationInfo) {
          pickupParams = {
            pickwareAddrFirstId: curLocationInfo?.provinceId,
            pickwareAddrSecondId: curLocationInfo?.cityId,
            pickwareAddrThirdId: curLocationInfo?.countyId,
            pickwareAddrFourthId: curLocationInfo?.townId,
            pickupAddrId: -1, // 暂定-1
            pickwareAddr: curLocationInfo?.fullAddressDetail,
            pickupLongitude: curLocationInfo?.longitude,
            pickupLatitude: curLocationInfo?.latitude,
            pickupContactName: curLocationInfo?.postHouseName,
            pickupContactTel: curLocationInfo?.phone,
            postcode: curLocationInfo?.postCode,
            stationName: curLocationInfo?.postHouseName,
            postHouseId: curLocationInfo?.postHouseId,
            pickUpStationAddressInfo: {
              ...curLocationInfo,
            },
          };
        } else {
          // 展示默认驿站
          pickupParams = {
            pickwareAddrFirstId: locationList[0]?.provinceId,
            pickwareAddrSecondId: locationList[0]?.cityId,
            pickwareAddrThirdId: locationList[0]?.countyId,
            pickwareAddrFourthId: locationList[0]?.townId,
            pickupAddrId: -1, // 暂定-1
            pickwareAddr: locationList[0]?.fullAddressDetail,
            pickupLongitude: locationList[0]?.longitude,
            pickupLatitude: locationList[0]?.latitude,
            pickupContactName: locationList[0]?.postHouseName,
            pickupContactTel: locationList[0]?.phone,
            postcode: locationList[0]?.postCode,
            stationName: locationList[0]?.postHouseName,
            postHouseId: locationList[0]?.postHouseId,
            pickUpStationAddressInfo: {
              ...locationList[0],
            },
          };
        }
      } else {
        // 选择上门取件地址
        if (selectedAddressInfo && !isDefault) {
          pickupParams = {
            pickwareAddrFirstId: selectedAddressInfo?.provinceId,
            pickwareAddrSecondId: selectedAddressInfo?.cityId,
            pickwareAddrThirdId: selectedAddressInfo?.districtId,
            pickwareAddrFourthId: selectedAddressInfo?.townId,
            pickupAddrId: curAddress,
            pickwareAddr: selectedAddressInfo?.fullAddress,
            pickupLongitude: selectedAddressInfo?.longitude,
            pickupLatitude: selectedAddressInfo?.latitude,
            pickupContactName: selectedAddressInfo?.name,
            pickupContactTel: selectedAddressInfo?.mobile,
            pickUpTelCountry: `+${selectedAddressInfo?.areaCode}`,
            reserveDateBegin: (curPickupTime.current as any)?.startTime,
            reserveDateEnd: (curPickupTime.current as any)?.endTime,
            reserveDate: (curPickupTime.current as any)?.promiseDate,
            batchId: (curPickupTime.current as any)?.batchId,
            productType: (curPickupTime.current as any)?.productType,
            postCode: selectedAddressInfo?.postCode,
            pickUpStationAddressInfo: {
              // addressDetail透传
              ...selectedAddressInfo,
              cityName: selectedAddressInfo?.urbanName,
              countyId: selectedAddressInfo?.districtId,
              addressDetail2: selectedAddressInfo?.addressLineTwo,
              fullAddressDetail: selectedAddressInfo?.fullAddress,
            },
          };
        } else {
          // 默认展示的地址
          pickupParams = {
            pickwareAddrFirstId: addressInfo?.provinceId,
            pickwareAddrSecondId: addressInfo?.cityId,
            pickwareAddrThirdId: addressInfo?.countyId,
            pickwareAddrFourthId: addressInfo?.townId,
            pickupAddrId: curAddress,
            pickwareAddr: addressInfo?.fullAddressDetail,
            pickupLongitude: addressInfo?.longitude,
            pickupLatitude: addressInfo?.latitude,
            pickupContactName: contactName,
            pickupContactTel: contactTel,
            pickUpTelCountry: contactTelCountry,
            reserveDateBegin: (curPickupTime.current as any)?.startTime,
            reserveDateEnd: (curPickupTime.current as any)?.endTime,
            reserveDate: (curPickupTime.current as any)?.promiseDate,
            batchId: (curPickupTime.current as any)?.batchId,
            productType: (curPickupTime.current as any)?.productType,
            postCode: addressInfo?.postCode,
            pickUpStationAddressInfo: {
              // fullAddressDetail addressDetail addressDetail2 透传
              ...addressInfo,
            },
          };
        }
      }
      recordApplyCreate({
        orderId,
        afsSkuQueries: wareList,
        customerExpect,
        returnModel: curMethod,
        refundType,
        questionDesc: inputValue,
        reasonId: curReason,
        ...pickupParams,
        images: imageList,
        videoIds: videoList as any,
        contactTelCountry,
        contactName,
        contactTel,
        returnAddr: addressInfo?.fullAddressDetail,
        returnAddrFirstId: addressInfo?.provinceId,
        returnAddrSecondId: addressInfo?.cityId,
        returnAddrThirdId: addressInfo?.countyId,
        returnAddrFourthId: addressInfo?.townId,
        returnAddrId: addressInfo?.addressId,
      })
        .then((recordApplyCreateData: any) => {
          if (recordApplyCreateData.data && recordApplyCreateData.success) {
            handleConfirmClick && handleConfirmClick(recordApplyCreateData.data);
          } else {
            UKToast.show({
              icon: 'warning',
              content:
                recordApplyCreateData.msg ||
                t('order.orderDetail.returnRequestModal.submitFailText'),
            });
          }
        })
        .catch(() => {
          UKToast.show({
            icon: 'error',
            content: t('order.orderDetail.returnRequestModal.submitErrorText'),
          });
        })
        .finally(() => {
          setSubmitLoading(false);
        });
    };

    const formatWareList =
      AfterSalesStore?.selectedProductList?.map(
        ({
          skuId,
          skuUUid,
          applyCount,
        }: {
          skuId: string;
          skuUUid: string;
          applyCount: number;
        }) => ({
          skuId,
          skuUUid,
          applyCount,
        }),
      ) || [];

    return (
      <>
        <UKDialog
          open={isOpen}
          onClose={closeHandler}
          showCloseBtn={true}
          className={styles.refund_modal}
        >
          <DialogTitle>{t('order.orderDetail.returnRequestModal.modalTitle')}</DialogTitle>
          <DialogContent className={styles.refundModalContent}>
            {loading ? (
              <div className={styles.loading_wrap}>
                <UKLoadingIcon type={CIRCLE_COLOR_MAP.BLACK} />
                <span className={styles.loading_text}>{t('common.pageStatus.loading')}</span>
              </div>
            ) : applyInfo?.returnModelList ? (
              <>
                <div className={styles.ruleContainer}>
                  <RuleWrap />
                </div>
                <div className={styles.refund_wrap}>
                  {/* reason */}
                  <div className={styles.refund_item_title}>
                    <div className={styles.refund_item_title_left}>
                      <span className={styles.require_style}>*</span>
                      {t('order.orderDetail.refundModal.reasonText')}
                    </div>
                  </div>
                  <div className={styles.reason_wrap}>
                    <CommonSelect
                      selectValue={curReason}
                      selectList={reasonList || []}
                      selectChangeHandler={selectChangeHandler}
                      placeholder={t('order.orderDetail.returnRequestModal.reasonPlaceholder')}
                    />
                  </div>
                  {/* detailed description */}
                  <div className={styles.refund_item_title}>
                    <div className={styles.refund_item_title_left}>
                      {/* <span className={styles.require_style}>*</span> */}
                      {t('order.orderDetail.returnRequestModal.descriptionText')}
                    </div>
                  </div>
                  <div className={styles.upload_wrap}>
                    {/* 上传图片 */}
                    {/* <UKUpload
                    moduleType="REVIEW"
                    onChange={(media: any) => onUploadMedia(media)}
                    limitNumber={5}
                  /> */}
                    <UKUpload
                      videoBussinessType='refund'
                      videoParam={{
                        skuIds,
                        orderId,
                      }}
                      type='image_video'
                      limitNumber={videoList?.length ? 4 : 5}
                      limitVideoNumber={(imageList?.length as any) > 4 ? 0 : 1}
                      apiType={IMAGEAPI_TYPE.AfterSale}
                      onChange={(media: any) => onUploadMedia(media)}
                      onVideoChange={(media: any) => {
                        const videos = media?.map((item: any) => String(item.vid));
                        if (videos?.length) {
                          setVideoList(videos);
                        } else {
                          setVideoList(null);
                        }
                      }}
                    />
                    <div className={styles.uploadTips}>
                      {t('order.orderDetail.returnRequestModal.uploadTips')}
                    </div>
                  </div>
                  <div className={styles.detail_wrap}>
                    <TextField
                      id='outlined-multiline-flexible'
                      multiline
                      maxRows={4}
                      minRows={4}
                      value={inputValue}
                      onChange={handleInputChange}
                      fullWidth
                      placeholder={t('order.orderDetail.returnRequestModal.detailPlaceholder')}
                      className='detail-textfield'
                      style={{
                        lineHeight: '20px',
                        color: '#1A1A1A',
                        fontSize: '16px',
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          background: '#f7f8fc',
                          paddingTop: '10px',
                          paddingBottom: '15px',
                        },
                        '& fieldset': {
                          border: 'none',
                        },
                      }}
                    />
                    <div
                      className={styles.input_tip}
                    >{`${inputValue.length}/${maxCharacters}`}</div>
                  </div>

                  {/* return method */}
                  <div className={styles.refund_item_title}>
                    <div className={styles.refund_item_title_left}>
                      <span className={styles.require_style}>*</span>
                      {t('order.orderDetail.returnRequestModal.returnMethodText')}
                    </div>
                  </div>
                  <div className={cn(styles.reason_wrap, styles.methodWrap)}>
                    {applyInfo?.returnModelList?.map((item, index) => {
                      return (
                        <div
                          className={cn(styles.methodItem, {
                            [styles.methodActiveItem]: String(item.type) === curMethod,
                          })}
                          key={index}
                          onClick={() => {
                            setCurMethod(String(item.type));
                          }}
                        >
                          <div className={styles.methodItemTitle}>{item.text}</div>
                          <div className={styles.methodItemDesc}>{item.desc}</div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Drop-off location */}
                  {curMethod === '70' ? (
                    <div
                      className={styles.dropOffWrap}
                      onClick={() => {
                        setShowLocationModal(true);
                      }}
                    >
                      <div className={styles.dropOffTitle}>
                        <div>{t('order.orderDetail.returnRequestModal.dropOffPoints')}</div>
                        <div className={styles.entryWrap}>
                          {t('order.orderDetail.returnRequestModal.dropOffPointsTips')}
                          <Expand className={styles.entryIco} />
                        </div>
                      </div>
                      {curLocationInfo && !isLocationDefault ? ( // 选择后的驿站
                        <div className={styles.dropOffContent}>
                          <div className={styles.dropOffContentMain}>
                            <LocationIco className={styles.locationIcoStyle} />
                            <span className={styles.locationName}>
                              {curLocationInfo.postHouseName}
                            </span>
                          </div>
                          {/* <div>{curLocationInfo.distance}</div> */}
                        </div>
                      ) : locationList[0]?.postHouseName ? ( // 初始化推荐的驿站
                        <div className={styles.dropOffContent}>
                          <div className={styles.dropOffContentMain}>
                            <span className={styles.recommendStyle}>
                              {t('order.orderDetail.returnRequestModal.recommended')}
                            </span>
                            <LocationIco className={styles.locationIcoStyle} />
                            <span className={styles.locationName}>
                              {locationList[0]?.postHouseName}
                            </span>
                          </div>
                          {/* <div>{locationList[0]?.distance}</div> */}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Pick-up address */}
                  {curMethod === '4' ? (
                    <>
                      <div className={styles.refund_item_title}>
                        <div className={styles.refund_item_title_left}>
                          <span className={styles.require_style}>*</span>
                          {t('order.orderDetail.returnRequestModal.pickUpText')}
                        </div>
                      </div>
                      <div className={styles.reason_wrap}>
                        <AddressSelect
                          selectValue={curAddress}
                          selectList={addressList || []}
                          selectChangeHandler={addressChangeHandler}
                          placeholder={t('order.orderDetail.returnRequestModal.addressPlaceholder')}
                          addAddressClick={() => setShowAddModal(true)}
                          defaultAddress={defaultAddress}
                          useDefault={isDefault}
                        />
                      </div>
                      <div className={styles.refund_item_title}>
                        <div className={styles.refund_item_title_left}>
                          <span className={styles.require_style}>*</span>
                          {t('order.orderDetail.returnRequestModal.pickUpTime')}
                        </div>
                      </div>
                      <div>
                        <DateSelect
                          requestData={
                            selectedAddressInfo && !isDefault
                              ? {
                                  orderId: String(orderId),
                                  pickwareAddrFirstId: String(selectedAddressInfo?.provinceId),
                                  pickwareAddrSecondId: String(selectedAddressInfo?.cityId),
                                  pickwareAddrThirdId: String(selectedAddressInfo?.districtId),
                                  pickwareAddrFourthId: String(selectedAddressInfo?.townId),
                                  pickwareAddr: selectedAddressInfo?.fullAddress,
                                  afsSkuQueries: formatWareList,
                                }
                              : {
                                  orderId: String(orderId),
                                  pickwareAddrFirstId:
                                    applyInfo?.returnCustomerInfo?.addressInfo?.provinceId,
                                  pickwareAddrSecondId:
                                    applyInfo?.returnCustomerInfo?.addressInfo?.cityId,
                                  pickwareAddrThirdId:
                                    applyInfo?.returnCustomerInfo?.addressInfo?.countyId,
                                  pickwareAddrFourthId:
                                    applyInfo?.returnCustomerInfo?.addressInfo?.townId,
                                  pickwareAddr:
                                    applyInfo?.returnCustomerInfo?.addressInfo?.fullAddressDetail,
                                  afsSkuQueries: formatWareList,
                                }
                          }
                          selectChangeHandler={timeChangeHandler}
                          cancelRequest={() => {
                            closeHandler();
                          }}
                        />
                      </div>
                    </>
                  ) : null}
                  {/* Estimated refund amount and method */}
                  {/* <div className={styles.content_wrap}>
                <div className={styles.refund_item_title}>
                  <div className={styles.refund_item_title_left}>
                    Estimated refund amount and method
                  </div>
                  <TipIcon className={styles.tip_ico} />
                </div>
                <CardAmount
                  amountInfo={{
                    cardNum: "8809 **** ****1290",
                    price: "7.33",
                    unit: "",
                  }}
                />
                <div className={styles.refund_item_sub_title}>
                  <div className={styles.refund_item_title_left}>
                    {1} coupon(s) would be returned
                  </div>
                  <TipIcon className={styles.tip_ico} />
                </div>
                <div className={styles.coupon_desc}>
                  1 coupon(s) for specific items will be returned as it meets the return rules.
                </div>
              </div> */}
                </div>
                <div className={styles.oper_wrap}>
                  <CommonButton
                    variant='outlined'
                    onClick={closeHandler}
                    style={{
                      marginRight: '12px',
                    }}
                  >
                    {t('order.orderList.common.cancelBtnText')}
                  </CommonButton>
                  <CommonButton
                    variant='contained'
                    onClick={createRecord}
                    disabled={submitLoading}
                    sx={{ boxShadow: 'none' }}
                  >
                    {t('order.orderList.common.submitBtnText')}
                  </CommonButton>
                </div>
              </>
            ) : (
              <UKPageNothing
                style={{ padding: '60px 0' }}
                defaultImageType={DefaultImageType.DEFAULT_NOTING}
                text={t('order.orderList.emptyPage.commonTip')}
              />
            )}
          </DialogContent>
        </UKDialog>
        {showAddModal && (
          <AddressAdd
            closeHandler={() => {
              setShowAddModal(false);
            }}
            newCreateSaveClose={saveCloseHandler}
          />
        )}
        {showLocationModal && (
          <LocationModal
            isOpen={showLocationModal}
            selectValue={curLocation}
            selectChangeHandler={locationChangeHandler}
            closeHandler={() => {
              setShowLocationModal(false);
            }}
            selectPostCode={curLocationInfo?.postCode || ''}
            initParams={getParamsMap(defaultAddress?.addressInfo)}
            orderId={orderId}
          />
        )}
      </>
    );
  },
);

export default ReturnRequestModal;
